import { useCallback, useMemo, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CancelModal from '@/app/common/components/CancelModal';
import { trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { PageFilterBar } from '../components/filters/PageFilterBar';
import { ExecutionDrawer } from '../components/executions/ExecutionDrawer';
import { ExecutionAlert } from '../components/executions/DuplicateExecutionNotice';
import { NoProject } from '../components/NoProject';
import {
  DEFAULT_EXECUTION_FILTERS,
  ExecutionFiltersBar,
  ExecutionStatusVisibilityFilters,
  matchesSearch,
  matchesStatusFilter,
  matchesVisibilityRange,
  normalizeSearchQuery,
  providerMatches,
  type ExecutionFiltersState
} from '../components/executions/ExecutionFiltersBar';
import { ExecutionsTable } from '../components/executions/ExecutionsTable';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useProject } from '../context/ProjectContext';
import { useTrackerAiPath } from '../utils/paths';

function ExecutionFilterEmptyState({ onClear }: { onClear: () => void }) {
  return (
    <Card className="border-dashed shadow-none">
      <Card.Body className="py-5 text-center d-flex flex-column align-items-center">
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-body-tertiary text-primary mb-3"
          style={{ width: '2.75rem', height: '2.75rem' }}
          aria-hidden
        >
          <FontAwesomeIcon icon="filter" />
        </span>
        <p className="fs-0 fw-semibold text-800 mb-2">اجرای مطابقی یافت نشد</p>
        <p className="fs--1 text-500 mb-3" style={{ maxWidth: '28rem' }}>
          جستجو، فیلترها یا بازه تاریخ را تنظیم کنید.
        </p>
        <Button type="button" size="sm" variant="falcon-default" onClick={onClear}>
          پاک کردن فیلترها
        </Button>
      </Card.Body>
    </Card>
  );
}

export function ExecutionsPage() {
  return (
    <NoProject>
      <ExecutionsContent />
    </NoProject>
  );
}

function ExecutionsContent() {
  const { data, loading, projectId, refresh } = useProject();
  const navigate = useNavigate();
  const toPath = useTrackerAiPath();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id');
  const [error, setError] = useState('');
  const [pendingPromptId, setPendingPromptId] = useState<string | null>(null);
  const [reextracting, setReextracting] = useState(false);

  const [filters, setFilters] = useState<ExecutionFiltersState>(DEFAULT_EXECUTION_FILTERS);
  const debouncedSearch = useDebouncedValue(filters.search, 300);
  const debouncedBrand = useDebouncedValue(filters.brand, 300);

  const history = data?.history ?? [];

  const openDrawer = useCallback(
    (id: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('id', id);
      setSearchParams(next, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  const closeDrawer = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  const brandSuggestions = useMemo(() => {
    const names = new Set<string>();
    for (const item of history) {
      for (const brand of item.topBrands ?? []) {
        names.add(brand.canonicalName);
      }
    }
    return [...names].sort((a, b) => a.localeCompare(b)).slice(0, 200);
  }, [history]);

  const filtered = useMemo(() => {
    const search = normalizeSearchQuery(debouncedSearch);
    const brandFilter = normalizeSearchQuery(debouncedBrand);

    return history.filter(item => {
      if (!matchesStatusFilter(item.status, filters.status)) return false;
      if (!providerMatches(item.provider, item.model, filters.provider)) return false;
      if (!matchesVisibilityRange(item.visibilityScore, filters.visibility)) return false;

      if (brandFilter) {
        const brandHit = (item.topBrands ?? []).some(brand =>
          matchesSearch(brandFilter, brand.canonicalName)
        );
        if (!brandHit) return false;
      }

      if (search) {
        const brandNames = (item.topBrands ?? []).map(b => b.canonicalName).join(' ');
        const hit = matchesSearch(
          search,
          item.promptText,
          item.provider,
          item.model,
          item.topicName,
          brandNames
        );
        if (!hit) return false;
      }

      return true;
    });
  }, [
    history,
    debouncedSearch,
    debouncedBrand,
    filters.status,
    filters.provider,
    filters.visibility
  ]);

  const noExecutions = !loading && history.length === 0;

  const requestReextract = useCallback((promptId: string) => {
    setError('');
    setPendingPromptId(promptId);
  }, []);

  async function confirmReextract() {
    if (!pendingPromptId) return;
    setReextracting(true);
    setError('');
    try {
      const report = await api.reextractBrands([pendingPromptId]);
      if (report.limited > 0) {
        setError(
          `سقف استخراج مجدد برند برای ${report.limited} اجرا تمام شده است.`,
        );
      }
      await refresh({ silent: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setReextracting(false);
    }
  }

  return (
    <div>
      <div className="mb-4">
        <PageFilterBar
          splitDateAndFilters
          extra={<ExecutionStatusVisibilityFilters filters={filters} onChange={setFilters} />}
        />
      </div>

      {error ? <ExecutionAlert message={error} /> : null}

      {noExecutions ? (
        <Card className="border-dashed">
          <Card.Body className="py-5 text-center d-flex flex-column align-items-center">
            <p className="fs-0 fw-semibold text-800 mb-2">هنوز اجرایی وجود ندارد</p>
            <p className="fs--1 text-500 mb-0" style={{ maxWidth: '28rem' }}>
              یک پرامپت اجرا کنید تا تاریخچه اجرا و جزئیات اشکال‌زدایی اینجا نمایش داده شود.
            </p>
            <Button
              size="sm"
              variant="primary"
              className="mt-3"
              onClick={() => navigate(toPath('prompts'))}
            >
              پرامپت‌ها
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <>
          <ExecutionFiltersBar
            filters={filters}
            onChange={setFilters}
            brandSuggestions={brandSuggestions}
          />

          {filtered.length === 0 && !loading ? (
            <ExecutionFilterEmptyState onClear={() => setFilters(DEFAULT_EXECUTION_FILTERS)} />
          ) : (
            <ExecutionsTable
              rows={filtered}
              loading={loading}
              emptyMessage={null}
              selectedId={selectedId}
              onView={openDrawer}
              onReextract={requestReextract}
            />
          )}
        </>
      )}

      {selectedId && (
        <ExecutionDrawer
          executionId={selectedId}
          projectId={projectId ?? undefined}
          onClose={closeDrawer}
        />
      )}

      <CancelModal
        show={pendingPromptId != null}
        setShow={() => setPendingPromptId(null)}
        title="استخراج مجدد برندها"
        submitTitle="استخراج مجدد"
        isSubmitting={reextracting}
        submitFunc={confirmReextract}
        desc="برندهای قبلی این پرامپت پاک می‌شود و استخراج دوباره روی اجراهای تکمیل‌شده آن اجرا می‌شود."
      />
    </div>
  );
}

export default ExecutionsPage;
